import {Injectable, QueryList} from '@angular/core';
import {UtilsService} from '../../appSharedModule/services/utils.service';
import {BwGraphUserConfigs, BwGraphUserConfigsService, BwGraphsUserConfigsService} from '../../generated';
import {HttpClient} from '@angular/common/http';
import {DatePipe} from '@angular/common';
import * as _ from 'lodash';
import moment from 'moment-timezone';
import {UIChart} from 'primeng/chart';
import {AuthenticationService} from '../../authenticationModule/services/authentication.service';
import {firstValueFrom, Observable} from 'rxjs';
import {map, tap} from 'rxjs/operators';

@Injectable()
export class GraphHandlerService {

  maxTime: number;
  minTime: number;
  unit = 'hour';
  protected utilsService: UtilsService;

  constructor(public authenticationService: AuthenticationService,
              private bwGraphUserConfigsService: BwGraphUserConfigsService,
              private bwGraphsUserConfigsService: BwGraphsUserConfigsService,
              protected http: HttpClient,
              private datePipe: DatePipe) {
    this.initMinAndMaxTime();
  }

  loadUserConfigs(): Observable<void> {
    return this.bwGraphsUserConfigsService.getBwGraphsUserConfigs().pipe(
      tap(response => {
        this.authenticationService.userCurrent.bwGraphUserConfigs = response.bwGraphUserConfigs;
        this.authenticationService.userCurrent.bwGraphUserDefaultConfig = response.bwGraphUserDefaultConfig;
      }),
      map(() => undefined as void)
    );
  }

  private _INFINIT_SCROLL_BATCH_SIZE = 4;

  get INFINIT_SCROLL_BATCH_SIZE(): number {
    return this._INFINIT_SCROLL_BATCH_SIZE;
  }

  private _periods = [
    {'value': 5, label: '5 min'},
    {'value': 60, label: '1 h'},
    {'value': 360, label: '6 h'},
    {'value': 1440, label: '1 day'},
    {'value': 2880, label: '2 day'}
  ];

  get periods(): any {
    return this._periods;
  }

  private _timeZoneList = [
    {label: 'GMT', value: 'UTC'},
    {label: 'Central European Time', value: 'CET'},
    {label: 'Central Standard Time/Central Daylight Time', value: 'CST6CDT'},
    {label: 'Japan Standard Time', value: 'Asia/Tokyo'},
    {label: 'Hong Kong Time', value: 'Asia/Hong_Kong'}
  ];

  get timeZoneList(): any {
    return this._timeZoneList;
  }

  private _zoomOptions = [
    {label: 'minute', value: 'minute'},
    {label: 'Hour', value: 'hour'},
    {label: 'Day', value: 'day'}
  ];

  get zoomOptions(): any {
    return this._zoomOptions;
  }

  getGraphLegend(index: string, dataList: any) {
    const data = _.find(dataList, {'value': index});
    if (data) {
      return data.label;
    } else {
      return index;
    }
  }

  initMinAndMaxTime(): { minTime: number, maxTime: number } {
    const currDate = new Date();
    this.maxTime = currDate.getTime();
    const endDate = new Date(currDate);
    endDate.setMinutes(endDate.getMinutes() - 60 * 6);
    this.minTime = endDate.getTime();

    return {minTime: this.minTime, maxTime: this.maxTime};
  }

  public getThemedGraphExecutionTimeOptions(onZoomComplete: (chart: any) => void) {
    return {
      animation: false,
      responsive: true,
      maintainAspectRatio: true,
      aspectRatio: 2.4,
      normalized: true,
      interaction: {
        mode: 'index',
        intersect: false
      },
      elements: {
        line: {
          tension: 0,
          borderWidth: 2,
          borderJoinStyle: 'round',
          borderCapStyle: 'round'
        },
        point: {
          radius: 0,
          hoverRadius: 4,
          hitRadius: 8,
          hoverBorderWidth: 2
        }
      },
      plugins: {
        legend: {
          display: false
        },
        tooltip: {
          backgroundColor: 'rgba(20, 20, 19, 0.92)',
          titleColor: '#ffffff',
          bodyColor: '#eceae4',
          borderColor: 'rgba(255, 255, 255, 0.15)',
          borderWidth: 1,
          cornerRadius: 6,
          padding: 10,
          usePointStyle: true,
          boxWidth: 12,
          boxHeight: 2,
          callbacks: {
            label: (context) => {
              const value = context.parsed.y;
              const formatted = (value === null || value === undefined) ? 'n/a' : Number(value).toLocaleString('en-US', {maximumFractionDigits: 3});
              return ' ' + context.dataset.label + ': ' + formatted;
            }
          }
        },
        zoom: {
          zoom: {
            wheel: {
              enabled: true,
              modifierKey: 'ctrl'
            },
            pinch: {
              enabled: true
            },
            drag: {
              enabled: true,
              backgroundColor: 'rgba(42, 120, 214, 0.12)',
              borderColor: '#2a78d6',
              borderWidth: 1
            },
            mode: 'x',
            onZoomComplete: (chart) => onZoomComplete(chart)
          },
          pan: {
            enabled: true,
            mode: 'x',
            modifierKey: 'shift',
            onPanComplete: (chart) => onZoomComplete(chart)
          }
        }
      },
      layout: {
        padding: {
          left: 0,
          right: 0,
          top: 8,
          bottom: 0
        }
      }
    };
  }

  formatAxisTick(value: any): any {
    if (typeof value === 'number' && Math.abs(value) >= 1000) {
      return new Intl.NumberFormat('en-US', {notation: 'compact', maximumFractionDigits: 1}).format(value);
    }
    return value;
  }

  setThemedMlrLatencyScales(minTime: number, unit: string, maxTime: number = null): any {
    return {
      x: {
        type: 'time',
        min: minTime,
        max: maxTime,
        time: {
          unit: unit,
          displayFormats: {
            day: 'MM-DD HH',
            hour: 'HH:mm',
            minute: 'HH:mm'
          }
        },
        grid: {
          display: false,
          borderColor: '#c3c2b7',
          tickColor: '#c3c2b7'
        },
        ticks: {
          color: '#898781',
          maxRotation: 0,
          autoSkipPadding: 16,
          font: {size: 11}
        }
      },
      Latency: {
        type: 'linear',
        position: 'left',
        beginAtZero: false,
        grid: {
          color: '#f0efec',
          borderColor: '#c3c2b7',
          tickColor: '#c3c2b7'
        },
        ticks: {
          color: '#898781',
          font: {size: 11},
          maxTicksLimit: 6,
          callback: (value) => this.formatAxisTick(value)
        },
        title: {
          display: true,
          text: 'Latency',
          color: '#52514e',
          font: {size: 11}
        }
      },
      MLR: {
        type: 'linear',
        position: 'right',
        min: 0,
        max: 100,
        grid: {
          drawOnChartArea: false,
          borderColor: '#c3c2b7',
          tickColor: '#c3c2b7'
        },
        ticks: {
          color: '#898781',
          font: {size: 11},
          maxTicksLimit: 5
        },
        title: {
          display: true,
          text: 'MLR',
          color: '#52514e',
          font: {size: 11}
        }
      }
    };
  }

  setThemedPlrLatencyScales(minTime: number, unit: string, maxTime: number = null): any {
    return {
      x: {
        type: 'time',
        min: minTime,
        max: maxTime,
        time: {
          unit: unit,
          displayFormats: {
            day: 'MM-DD HH',
            hour: 'HH:mm',
            minute: 'HH:mm'
          }
        },
        grid: {
          display: false,
          borderColor: '#c3c2b7',
          tickColor: '#c3c2b7'
        },
        ticks: {
          color: '#898781',
          maxRotation: 0,
          autoSkipPadding: 16,
          font: {size: 11}
        }
      },
      Latency: {
        type: 'linear',
        position: 'left',
        beginAtZero: false,
        grid: {
          color: '#f0efec',
          borderColor: '#c3c2b7',
          tickColor: '#c3c2b7'
        },
        ticks: {
          color: '#898781',
          font: {size: 11},
          maxTicksLimit: 6,
          callback: (value) => this.formatAxisTick(value)
        },
        title: {
          display: true,
          text: 'Latency',
          color: '#52514e',
          font: {size: 11}
        }
      },
      PLR: {
        type: 'linear',
        position: 'right',
        min: 0,
        max: 100,
        grid: {
          drawOnChartArea: false,
          borderColor: '#c3c2b7',
          tickColor: '#c3c2b7'
        },
        ticks: {
          color: '#898781',
          font: {size: 11},
          maxTicksLimit: 5
        },
        title: {
          display: true,
          text: 'PLR',
          color: '#52514e',
          font: {size: 11}
        }
      }
    };
  }

  getCommonOption() {
    return {
      animation: {
        duration: 0,
      },
      hover: {
        animationDuration: 0,
      },
      responsiveAnimationDuration: 0,
    };
  }

  async loadSavedFilterConfiguration(
    savedUserConfigs: BwGraphUserConfigs[],
    selectedConfigurationId: string,
    bwGraphUserDefaultConfig: BwGraphUserConfigs,
    markets2TransportOrChannels2Pop: any[],
    defaultGraphExecutionTimeOptions: any,
    market2TransportOrChannel2PopListSelect: any[],
    checkboxesModel: any,
    charts: QueryList<UIChart>,
    configurationToLoad: BwGraphUserConfigs | null = null,
    showChangedConfigMessage: boolean
  ): Promise<any> {
    const UserSavedConfiguration = (!configurationToLoad) ? _.find(savedUserConfigs, {id: selectedConfigurationId}) as BwGraphUserConfigs : configurationToLoad;

    const userDefaultGraphsParams = _.cloneDeep(UserSavedConfiguration.config);
    const selectedConfigurationName = _.clone(UserSavedConfiguration.configName);
    let graphExecutionTimeOptions;
    let selectedTimeZone;
    let selectedMarkets2TransportOrChannels2Pop;
    let selectedMarketToTransportOrChannels2PopForList;
    let zoomIn;
    let selectedPeriod;

    if (!configurationToLoad) {
      const result = await this.updateDefaultConfiguration([bwGraphUserDefaultConfig], UserSavedConfiguration, bwGraphUserDefaultConfig, selectedConfigurationId, showChangedConfigMessage);
      bwGraphUserDefaultConfig = result.bwGraphUserDefaultConfig;
      selectedConfigurationId = result.selectedConfigurationId;
      showChangedConfigMessage = result.showChangedConfigMessage;
    }

    if (!userDefaultGraphsParams.graphOption) {
      graphExecutionTimeOptions = {};
      _.forEach(markets2TransportOrChannels2Pop, (market2TransportOrChannel2Pop) => {
        graphExecutionTimeOptions[market2TransportOrChannel2Pop] = _.cloneDeep(defaultGraphExecutionTimeOptions);
      });
    } else {
      graphExecutionTimeOptions = userDefaultGraphsParams.graphOption;
      _.forEach(markets2TransportOrChannels2Pop, (market2TransportOrChannel2Pop) => {
        graphExecutionTimeOptions = this.loadConfigurations(
          market2TransportOrChannel2Pop,
          graphExecutionTimeOptions,
          defaultGraphExecutionTimeOptions,
          userDefaultGraphsParams,
          this.minTime
        );
      });
      selectedTimeZone = userDefaultGraphsParams.timeZone;
      moment.tz.setDefault(userDefaultGraphsParams.timeZone);
      if (userDefaultGraphsParams.selectedVvlans.length > 0) {
        selectedMarkets2TransportOrChannels2Pop = userDefaultGraphsParams.selectedVvlans;
        selectedMarketToTransportOrChannels2PopForList = this.createSelectedDataGraphList(market2TransportOrChannel2PopListSelect, selectedMarkets2TransportOrChannels2Pop);
        _.forEach(selectedMarkets2TransportOrChannels2Pop, (selectedMarket2TransportOrChannel2Pop) => {
          checkboxesModel[selectedMarket2TransportOrChannel2Pop] = true;
        });
      }
    }

    zoomIn = userDefaultGraphsParams.zoomIn;
    if (userDefaultGraphsParams.selectedPeriod) {
      selectedPeriod = userDefaultGraphsParams.selectedPeriod;
    }

    charts.map((chart: UIChart) => {
      chart.options = userDefaultGraphsParams.graphOption[chart.data.id];
      chart.reinit();
    });
    this.saveUserPrefs(
      userDefaultGraphsParams,
      charts,
      selectedMarketToTransportOrChannels2PopForList,
      selectedMarkets2TransportOrChannels2Pop,
      selectedPeriod,
      zoomIn,
      selectedConfigurationId,
      selectedTimeZone
    );
    return {
      userDefaultGraphsParams,
      selectedConfigurationName,
      graphExecutionTimeOptions,
      selectedTimeZone,
      selectedMarkets2TransportOrChannels2Pop,
      selectedMarketToTransportOrChannels2PopForList,
      zoomIn,
      selectedPeriod,
      loadingPrefs: false,
      showChangedConfigMessage: false
    };
  }

  saveUserPrefs(userDefaultGraphsParams: any, charts: QueryList<UIChart>, selectedMarketToTransportOrChannels2PopForList: any[], selectedMarkets2TransportOrChannels2Pop: any[], selectedPeriod: any, zoomIn: any, selectedConfigurationId: string, selectedTimeZone: string): any {
    const showChangedConfigMessage = true;
    const updateRoutesDisabled = true;

    charts.map((chart: UIChart) => {
      userDefaultGraphsParams.graphOption[chart.data.id] = chart.options;
    });
    selectedMarkets2TransportOrChannels2Pop = selectedMarketToTransportOrChannels2PopForList.map(({value}) => value);
    userDefaultGraphsParams.selectedVvlans = selectedMarkets2TransportOrChannels2Pop;
    userDefaultGraphsParams.selectedPeriod = selectedPeriod;
    userDefaultGraphsParams.zoomIn = zoomIn;
    userDefaultGraphsParams.selectedConfiguration = selectedConfigurationId;
    userDefaultGraphsParams.timeZone = selectedTimeZone;

    return {
      showChangedConfigMessage,
      updateRoutesDisabled,
      userDefaultGraphsParams,
      charts,
      selectedMarkets2TransportOrChannels2Pop
    };
  }

  async updateDefaultConfiguration(
    oldConfigurations: Array<BwGraphUserConfigs>,
    newConfiguration: BwGraphUserConfigs = null,
    bwGraphUserDefaultConfig: BwGraphUserConfigs,
    selectedConfigurationId: string,
    showChangedConfigMessage: boolean
  ): Promise<{
    selectedConfigurationId: string;
    bwGraphUserDefaultConfig: BwGraphUserConfigs;
    showChangedConfigMessage: boolean
  }> {
    for (const oldConfiguration of oldConfigurations) {
      if (oldConfiguration && oldConfiguration.type === 'market-data-qrd-mlr-graph') {
        await firstValueFrom(this.bwGraphUserConfigsService.putBwGraphUserConfigsItem(oldConfiguration.id, {isDefault: false}));
        bwGraphUserDefaultConfig = null;
      }
    }

    if (newConfiguration && newConfiguration.type === 'market-data-qrd-mlr-graph') {
      try {
        const result = await firstValueFrom(this.bwGraphUserConfigsService.putBwGraphUserConfigsItem(newConfiguration.id, {isDefault: true}));
        bwGraphUserDefaultConfig = result;
        selectedConfigurationId = result.id;
        showChangedConfigMessage = false;
      } catch (error) {
        this.utilsService.handleErrorWebservice$(error);
      }
    } else {
      this.authenticationService.userCurrent.bwGraphUserDefaultConfig = null;
    }

    return {bwGraphUserDefaultConfig, selectedConfigurationId, showChangedConfigMessage};
  }

  loadConfigurations(
    index: any,
    graphExecutionTimeOptions: any,
    defaultGraphExecutionTimeOptions: any,
    userDefaultGraphsParams: any,
    minTime: number
  ): any {
    if (!graphExecutionTimeOptions[index]) {
      graphExecutionTimeOptions[index] = _.cloneDeep(defaultGraphExecutionTimeOptions);
    } else {
      graphExecutionTimeOptions[index]['plugins'] = defaultGraphExecutionTimeOptions['plugins'];
    }

    const endDate = new Date();
    const maxTime = endDate.getTime();
    endDate.setMinutes(endDate.getMinutes() - userDefaultGraphsParams.selectedPeriod[index]);
    let endDateString = endDate.getTime();
    let zoomInterval = null;

    if (graphExecutionTimeOptions[index]['scales'].x.min &&
      graphExecutionTimeOptions[index]['scales'].x.max &&
      graphExecutionTimeOptions[index]['scales'].x.min < minTime) {
      zoomInterval = graphExecutionTimeOptions[index]['scales'].x.max -
        graphExecutionTimeOptions[index]['scales'].x.min;
      endDateString = maxTime - zoomInterval;
      graphExecutionTimeOptions[index]['scales'].x.min = endDateString;
      graphExecutionTimeOptions[index]['scales'].x.max = maxTime;
    }

    return graphExecutionTimeOptions;
  }

  createSelectedDataGraphList(dataGraphListSelect, selectedDataGraph) {
    return dataGraphListSelect.filter(channel2PopSelectItem => selectedDataGraph.includes((channel2PopSelectItem.value).toString()) || selectedDataGraph.includes(channel2PopSelectItem.value));
  }

  computeSeriesStats(values: any[]): { min: number; max: number; avg: number } | null {
    const numericValues = values
      .filter(value => value !== null && value !== undefined && value !== '')
      .map(value => Number(value))
      .filter(value => isFinite(value));
    if (numericValues.length === 0) {
      return null;
    }
    return {
      min: _.min(numericValues),
      max: _.max(numericValues),
      avg: _.sum(numericValues) / numericValues.length
    };
  }

  downloadFile(href: string, fileName: string): void {
    const link = document.createElement('a');
    link.href = href;
    link.download = fileName;
    link.click();
  }

  buildExportFileName(label: string, suffix: string, extension: string): string {
    const sanitizedLabel = label.replace(/[^a-zA-Z0-9_-]+/g, '-');
    return sanitizedLabel + suffix + this.datePipe.transform(new Date(), 'yyyyMMdd-HHmmss') + '.' + extension;
  }

  extractTechnicalPop(label: string, pattern: RegExp): string | null {
    const match = label.match(pattern);
    return match ? match[1] : null;
  }

  reinitializeCheckBoxModel(data: any[], selectedData: any[], checkboxesModel: any): void {
    _.forEach(data, (index) => {
      checkboxesModel[index] = false;
    });
    _.forEach(selectedData, (selected) => {
      checkboxesModel[selected] = true;
    });
  }

  reanchorRelativeTimeWindow(index: string, selectedPeriod: any, graphExecutionTimeOptions: any): void {
    const period = selectedPeriod[index];
    if (period === undefined || period === 'all' || !graphExecutionTimeOptions[index]) {
      return;
    }
    const endDate = new Date();
    const maxTime = endDate.getTime();
    endDate.setMinutes(endDate.getMinutes() - period);
    graphExecutionTimeOptions[index].scales.x.min = endDate.getTime();
    graphExecutionTimeOptions[index].scales.x.max = maxTime;
  }

  applyThemeToSavedOptions(savedOptionsByGraph: any, defaultGraphExecutionTimeOptions: any): any {
    _.forEach(savedOptionsByGraph, (savedOptions, index) => {
      const themedOptions = _.cloneDeep(defaultGraphExecutionTimeOptions);
      const savedXScale = savedOptions && savedOptions.scales && savedOptions.scales.x;
      if (savedXScale) {
        if (savedXScale.min !== undefined && savedXScale.min !== null
          && savedXScale.max !== undefined && savedXScale.max !== null) {
          themedOptions['scales'].x.min = savedXScale.min;
          themedOptions['scales'].x.max = savedXScale.max;
        }
        if (savedXScale.time && savedXScale.time.unit) {
          themedOptions['scales'].x.time.unit = savedXScale.time.unit;
        }
      }
      savedOptionsByGraph[index] = themedOptions;
    });
    return savedOptionsByGraph;
  }

  applyZoomComplete(graphExecutionTimeOptions: any, chart: any, onSave: () => void): void {
    graphExecutionTimeOptions[chart.chart.config.data.id]['scales'] = chart.chart.config.options.scales;
    onSave();
  }

  applyResetZoom(vvlan: string, charts: QueryList<UIChart>, selectedPeriod: any, minTime: number, maxTime: number): void {
    selectedPeriod[vvlan] = 360;
    charts.map((chart: UIChart) => {
      if (chart.data.id === vvlan) {
        chart.options.scales.x.max = maxTime;
        chart.options.scales.x.min = minTime;
        chart.reinit();
      }
    });
  }

  applyTimeZoneChange(selectedTimeZone: string, charts: QueryList<UIChart>): string {
    const normalizedTimeZone = (selectedTimeZone === null || selectedTimeZone === undefined) ? 'UTC' : selectedTimeZone;
    moment.tz.setDefault(normalizedTimeZone);
    charts.map((chart: UIChart) => chart.reinit());
    return normalizedTimeZone;
  }

  applyTimeScaleUpdate(index: string, charts: QueryList<UIChart>, selectedPeriod: any, minTime: number, userDefaultGraphsParams: any, onSave: () => void): void {
    if (selectedPeriod[index] === 'all') {
      charts.map((chart: UIChart) => {
        if (chart.data.id === index) {
          userDefaultGraphsParams.graphOption[chart.data.id] = chart.options;
          userDefaultGraphsParams.selectedPeriod = selectedPeriod;
          chart.options.scales.x.min = minTime;
          chart.options.scales.x.max = null;
          chart.reinit();
          onSave();
        }
      });
    } else {
      const endDate = new Date();
      const maxTime = endDate.getTime();
      endDate.setMinutes(endDate.getMinutes() - selectedPeriod[index]);
      const endDateString = endDate.getTime();
      charts.map((chart: UIChart) => {
        if (chart.data.id === index) {
          chart.options.scales.x.min = endDateString;
          chart.options.scales.x.max = maxTime;
          userDefaultGraphsParams.selectedPeriod[index] = selectedPeriod[index];
          userDefaultGraphsParams.graphOption[chart.data.id] = chart.options;
          chart.reinit();
          onSave();
        }
      });
    }
  }

  exportChartPng(charts: QueryList<UIChart>, index: string, fileName: string): void {
    charts.map((chart: UIChart) => {
      if (chart.data.id === index && chart.chart) {
        const sourceCanvas = chart.chart.canvas;
        const exportCanvas = document.createElement('canvas');
        exportCanvas.width = sourceCanvas.width;
        exportCanvas.height = sourceCanvas.height;
        const ctx = exportCanvas.getContext('2d');
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, exportCanvas.width, exportCanvas.height);
        ctx.drawImage(sourceCanvas, 0, 0);
        this.downloadFile(exportCanvas.toDataURL('image/png'), fileName);
      }
    });
  }

  exportTwoSeriesCsv(graphData: any, index: string, metricLabel: string, fileName: string): void {
    const data = graphData[index];
    if (!data || !data.datasets || data.datasets.length === 0) {
      return;
    }
    const rows = ['Date;' + metricLabel + ';Latency'];
    _.forEach(data.labels, (timestamp, i) => {
      const date = moment(timestamp).format('YYYY-MM-DD HH:mm:ss');
      rows.push(date + ';' + data.datasets[0].data[i] + ';' + data.datasets[1].data[i]);
    });
    const blob = new Blob([rows.join('\n')], {type: 'text/csv;charset=utf-8'});
    const url = URL.createObjectURL(blob);
    this.downloadFile(url, fileName);
    URL.revokeObjectURL(url);
  }

  applyGraphLoadError(graphData: any, index: string): void {
    graphData[index] = {id: index, labels: 'No data available', datasets: []};
  }

  applyTwoSeriesGraphResponse(
    response: any[],
    index: string,
    metricLabel: string,
    metricColor: string,
    latencyColor: string,
    graphData: any,
    graphStats: any,
    charts: QueryList<UIChart>,
    forceRefresh: boolean,
    reanchor: (idx: string) => void
  ): void {
    const metricValues = response.map(data => data.value);
    const latencyValues = response.map(data => data.latency);
    graphStats[index] = {
      [metricLabel.toLowerCase()]: this.computeSeriesStats(metricValues),
      latency: this.computeSeriesStats(latencyValues)
    };
    const existingGraphData = graphData[index];
    if (forceRefresh && existingGraphData && existingGraphData.datasets && existingGraphData.datasets.length === 2) {
      existingGraphData.labels = response.map(data => data.date * 1000);
      existingGraphData.datasets[0].data = metricValues;
      existingGraphData.datasets[1].data = latencyValues;
      charts.forEach((chart: UIChart) => {
        if (chart.data.id === index) {
          reanchor(index);
          chart.reinit();
        }
      });
    } else {
      graphData[index] = {
        id: index, labels: response.map(data => data.date * 1000), datasets: [{
          'label': metricLabel,
          'borderColor': metricColor,
          'backgroundColor': metricColor,
          'yAxisID': metricLabel,
          'tension': 0,
          'fill': false,
          'data': metricValues,
          'pointHoverBackgroundColor': metricColor,
          'pointHoverBorderColor': '#ffffff',
        }, {
          'label': 'Latency',
          'borderColor': latencyColor,
          'backgroundColor': latencyColor,
          'yAxisID': 'Latency',
          'tension': 0,
          'fill': false,
          'data': latencyValues,
          'pointHoverBackgroundColor': latencyColor,
          'pointHoverBorderColor': '#ffffff',
        }]
      };
    }
  }
}
